import React, { useEffect, useState, useMemo } from "react";
import { CaretLeft, CaretRight, Barbell, Heart } from "@phosphor-icons/react";
import api from "@/lib/api";

const DAYS = ["S", "M", "T", "W", "T", "F", "S"];
const MONTHS = ["January", "February", "March", "April", "May", "June", "July", "August", "September", "October", "November", "December"];

function pad(n) { return String(n).padStart(2, "0"); }

export default function Calendar() {
  const today = new Date();
  const [view, setView] = useState({ year: today.getFullYear(), month: today.getMonth() }); // month is 0-indexed
  const [events, setEvents] = useState([]);
  const [selectedDay, setSelectedDay] = useState(null);

  const monthStart = useMemo(() => new Date(view.year, view.month, 1), [view]);
  const monthEnd = useMemo(() => new Date(view.year, view.month + 1, 0), [view]);

  useEffect(() => {
    const start = `${view.year}-${pad(view.month + 1)}-01`;
    const end = `${view.year}-${pad(view.month + 1)}-${pad(monthEnd.getDate())}`;
    api.get("/calendar", { params: { start, end } }).then(({ data }) => setEvents(data));
  }, [view, monthEnd]);

  const byDate = useMemo(() => {
    const map = {};
    events.forEach((e) => { map[e.date] = e; });
    return map;
  }, [events]);

  // Build grid: leading empties + days
  const firstWeekday = monthStart.getDay();
  const daysInMonth = monthEnd.getDate();
  const cells = [];
  for (let i = 0; i < firstWeekday; i++) cells.push(null);
  for (let d = 1; d <= daysInMonth; d++) cells.push(d);

  const move = (delta) => {
    let m = view.month + delta;
    let y = view.year;
    if (m < 0) { m = 11; y -= 1; }
    if (m > 11) { m = 0; y += 1; }
    setView({ year: y, month: m });
    setSelectedDay(null);
  };

  const selectedDateStr = selectedDay
    ? `${view.year}-${pad(view.month + 1)}-${pad(selectedDay)}`
    : null;
  const selectedEvent = selectedDateStr ? byDate[selectedDateStr] : null;

  const isToday = (d) =>
    today.getFullYear() === view.year && today.getMonth() === view.month && today.getDate() === d;

  return (
    <div className="p-6 lg:p-10 max-w-7xl mx-auto" data-testid="calendar-page">
      <div className="mb-8">
        <div className="label-tiny text-brand-accent mb-2">// TIMELINE</div>
        <h1 className="font-heading text-4xl sm:text-5xl">TRAINING CALENDAR</h1>
      </div>

      <div className="grid lg:grid-cols-3 gap-6">
        <div className="lg:col-span-2">
          <div className="tactical-card p-6">
            <div className="flex items-center justify-between mb-6">
              <h2 className="font-heading text-2xl">{MONTHS[view.month]} <span className="text-brand-accent tabular-nums">{view.year}</span></h2>
              <div className="flex gap-2">
                <button onClick={() => move(-1)} className="border border-white/10 hover:border-brand-accent p-2 transition-colors" data-testid="calendar-prev-button">
                  <CaretLeft size={16} />
                </button>
                <button
                  onClick={() => { setView({ year: today.getFullYear(), month: today.getMonth() }); setSelectedDay(null); }}
                  className="border border-white/10 hover:border-brand-accent px-3 text-xs label-tiny tracking-wider transition-colors"
                  data-testid="calendar-today-button"
                >
                  TODAY
                </button>
                <button onClick={() => move(1)} className="border border-white/10 hover:border-brand-accent p-2 transition-colors" data-testid="calendar-next-button">
                  <CaretRight size={16} />
                </button>
              </div>
            </div>

            <div className="grid grid-cols-7 gap-1 mb-2">
              {DAYS.map((d, i) => (
                <div key={i} className="label-tiny text-center py-2 text-text-secondary">{d}</div>
              ))}
            </div>
            <div className="grid grid-cols-7 gap-1" data-testid="calendar-grid">
              {cells.map((d, i) => {
                if (d === null) return <div key={i} className="aspect-square" />;
                const dateStr = `${view.year}-${pad(view.month + 1)}-${pad(d)}`;
                const ev = byDate[dateStr];
                const has = ev && (ev.workouts.length > 0 || ev.recovery !== null);
                const isSelected = selectedDay === d;
                return (
                  <button
                    key={i}
                    onClick={() => setSelectedDay(d)}
                    className={`aspect-square border text-sm relative transition-all ${
                      isSelected
                        ? "border-brand-accent bg-brand-accent/10"
                        : isToday(d)
                        ? "border-brand-primary"
                        : "border-white/5 hover:border-white/30"
                    }`}
                    data-testid={`calendar-day-${d}`}
                  >
                    <span className={`absolute top-1 left-1.5 text-xs tabular-nums ${isToday(d) ? "text-brand-accent font-bold" : "text-text-secondary"}`}>{d}</span>
                    {has && (
                      <div className="absolute bottom-1 left-1 right-1 flex gap-0.5 justify-center">
                        {ev.workouts.length > 0 && <span className="w-1.5 h-1.5 rounded-full bg-brand-accent" />}
                        {ev.recovery !== null && <span className="w-1.5 h-1.5 rounded-full bg-brand-primary" />}
                      </div>
                    )}
                  </button>
                );
              })}
            </div>

            <div className="flex gap-4 mt-4 label-tiny">
              <span className="flex items-center gap-1.5"><span className="w-2 h-2 rounded-full bg-brand-accent" /> WORKOUT</span>
              <span className="flex items-center gap-1.5"><span className="w-2 h-2 rounded-full bg-brand-primary" /> RECOVERY</span>
            </div>
          </div>
        </div>

        <div className="lg:col-span-1">
          <div className="tactical-card p-6" data-testid="calendar-detail-panel">
            <div className="label-tiny mb-3">
              {selectedDateStr ? `// ${new Date(selectedDateStr + "T12:00:00").toDateString().toUpperCase()}` : "// SELECT A DAY"}
            </div>
            {!selectedDateStr && (
              <p className="text-sm text-text-secondary">Tap a date to see workouts and recovery logged that day.</p>
            )}
            {selectedDateStr && !selectedEvent && (
              <div className="text-center py-8">
                <Barbell size={32} className="text-text-secondary mx-auto mb-3 opacity-40" />
                <p className="text-sm text-text-secondary">Rest day. No activity logged.</p>
              </div>
            )}
            {selectedEvent && (
              <div className="space-y-4">
                {selectedEvent.workouts.length > 0 && (
                  <div>
                    <div className="label-tiny mb-2 flex items-center gap-1"><Barbell size={12} weight="fill" className="text-brand-accent" /> WORKOUTS</div>
                    <div className="space-y-2">
                      {selectedEvent.workouts.map((w) => (
                        <div key={w.log_id} className="border border-white/10 p-3" data-testid={`day-workout-${w.log_id}`}>
                          <div className="font-heading text-base">{w.name}</div>
                          <div className="flex items-center gap-3 text-xs text-text-secondary mt-1">
                            <span className="label-tiny text-brand-accent">{w.category}</span>
                            <span className="tabular-nums">{w.duration_min} MIN</span>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
                {selectedEvent.recovery !== null && (
                  <div>
                    <div className="label-tiny mb-2 flex items-center gap-1"><Heart size={12} weight="fill" className="text-brand-primary" /> RECOVERY</div>
                    <div className="font-heading text-3xl tabular-nums text-brand-accent">{selectedEvent.recovery}<span className="text-base text-text-secondary">/100</span></div>
                  </div>
                )}
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
